import { Component, computed, effect, input, model } from "@angular/core"
import { NgClass } from "@angular/common"
import { FormValueControl } from "@angular/forms/signals"
import { MatDatepickerModule } from "@angular/material/datepicker"
import { Observable } from "rxjs"

@Component({
    selector: "valben-date-time-picker",
    templateUrl: "./date-time-picker.component.html",
    standalone: true,
    imports: [NgClass, MatDatepickerModule],
})
export class DateTimePickerComponent implements FormValueControl<string> {
    value = model("")
    title = input("")
    optional = input(true)
    error = input(false)
    invalid = input(false)

    hasError = computed(() => this.error() || this.invalid())
    showTodayButton = input(true)
    dateOnly = input(false)
    minDate = input<Date | undefined>(undefined)
    maxDate = input<Date | undefined>(undefined)

    todayInRange = computed(() => {
        const today = this._startOfDay(new Date())
        const min = this.minDate()
        const max = this.maxDate()
        return (!min || today >= this._startOfDay(min)) && (!max || today <= this._startOfDay(max))
    })

    key = input("")
    itemObservable = input<(() => Observable<any> | undefined) | undefined>(undefined)
    update = input<((value: any) => void) | undefined>(undefined)

    date: Date | undefined = undefined

    constructor() {
        effect(() => {
            const incoming = this.value()
            if (incoming !== this._serialize(this.date)) this.date = this._parse(incoming)
        })

        effect(onCleanup => {
            const sub = this.itemObservable()?.()?.subscribe(item => {
                if (item == null) return
                this.value.set(this._serialize(this._parse(item[this.key()])))
            })
            onCleanup(() => sub?.unsubscribe())
        })
    }

    onSelect(date: Date | null): void {
        this.date = date ?? new Date()
        const serialized = this._serialize(this.date)
        this.value.set(serialized)
        this.update()?.(serialized)
    }

    onClear(): void {
        this.date = undefined
        this.value.set("")
        this.update()?.("")
    }

    setToday(): void {
        this.onSelect(new Date())
    }

    private _startOfDay(date: Date): number {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
    }

    private _parse(value: string | Date | null | undefined): Date | undefined {
        if (!value) return undefined

        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? undefined : value
        }

        if (this.dateOnly()) {
            const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value).trim().slice(0, 10))
            if (!match) return undefined
            return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
        }

        const parsed = new Date(value)
        return Number.isNaN(parsed.getTime()) ? undefined : parsed
    }

    private _serialize(date?: Date): string {
        if (!date) return ""

        if (this.dateOnly()) {
            const month = `${date.getMonth() + 1}`.padStart(2, "0")
            const day = `${date.getDate()}`.padStart(2, "0")
            return `${date.getFullYear()}-${month}-${day}`
        }

        return date.toISOString()
    }
}
